import { useEffect, useState } from "react";
import { apiClient } from "../lib/api";
import MotorcycleCard from "../components/MotorcycleCard";
import Seo from "../components/Seo";
import { Search } from "lucide-react";

const Motorcycles = ({ condition }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("newest");

  const isSifir = condition === "sifir";

  useEffect(() => {
    setLoading(true);
    apiClient
      .get("/motorcycles", { params: { condition, limit: 200 } })
      .then((r) => setItems(r.data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [condition]);

  const filtered = items
    .filter((m) => {
      const q = search.toLowerCase().trim();
      if (!q) return true;
      return (
        m.brand?.toLowerCase().includes(q) ||
        m.model?.toLowerCase().includes(q) ||
        String(m.year).includes(q)
      );
    })
    .sort((a, b) => {
      if (sort === "price-asc") return a.price - b.price;
      if (sort === "price-desc") return b.price - a.price;
      if (sort === "year-desc") return b.year - a.year;
      return new Date(b.created_at) - new Date(a.created_at);
    });

  return (
    <div data-testid="motorcycles-page" className="bg-zinc-950 text-white pt-32 pb-24 min-h-screen">
      <Seo
        title={isSifir ? "Sıfır Motorlar" : "İkinci El Motorlar"}
        description={
          isSifir
            ? "İstanbul'da sıfır kilometre, garantili motosiklet modelleri. Gensan Motors'ta uygun fiyatlı sıfır motor seçeneklerini keşfedin ve hemen sahip olun."
            : "İstanbul'da ekspertizden geçmiş, güvenilir ikinci el motosiklet ilanları. Gensan Motors'ta uygun fiyatlı 2. el motor seçeneklerini inceleyin."
        }
        keywords={isSifir ? "sıfır motor satışı, sıfır motosiklet İstanbul, garantili motosiklet" : "ikinci el motor satışı, 2. el motosiklet, ekspertizli ikinci el motor"}
        path={isSifir ? "/sifir-motorlar" : "/ikinci-el-motorlar"}
      />
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <span className="block h-px w-12 bg-orange-500" />
            <span className="text-orange-500 font-bold uppercase tracking-[0.3em] text-xs">
              {isSifir ? "Sıfır" : "İkinci El"}
            </span>
          </div>
          <h1 className="font-display text-5xl md:text-7xl font-bold uppercase leading-none tracking-tight">
            {isSifir ? (
              <>Sıfır <span className="text-orange-500">Motorlar</span></>
            ) : (
              <>İkinci El <span className="text-orange-500">Motorlar</span></>
            )}
          </h1>
          <p className="mt-6 text-zinc-400 max-w-2xl text-lg">
            {isSifir
              ? "Sıfır kilometre, garantili motosiklet seçeneklerimizi keşfedin."
              : "Ekspertizden geçmiş, güvenilir ikinci el motosiklet stoğumuzu inceleyin."}
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-10 border-y border-zinc-900 py-5">
          <div className="relative flex-1">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              data-testid="search-input"
              type="text"
              placeholder="Marka, model veya yıl ara..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full bg-transparent border border-zinc-800 hover:border-zinc-700 focus:border-orange-500 outline-none pl-12 pr-4 py-3 text-sm uppercase tracking-wide placeholder:text-zinc-600 transition-colors"
            />
          </div>
          <select
            data-testid="sort-select"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="bg-transparent border border-zinc-800 hover:border-zinc-700 focus:border-orange-500 outline-none px-4 py-3 text-sm uppercase tracking-wide text-white cursor-pointer transition-colors"
          >
            <option value="newest" className="bg-black">En Yeni</option>
            <option value="price-asc" className="bg-black">Fiyat ↑</option>
            <option value="price-desc" className="bg-black">Fiyat ↓</option>
            <option value="year-desc" className="bg-black">Yıl ↓</option>
          </select>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <div key={i} className="aspect-[4/3] bg-zinc-900 animate-pulse" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="border border-zinc-900 p-20 text-center">
            <p className="text-zinc-400 uppercase tracking-widest text-sm">
              {items.length === 0
                ? "Henüz motor eklenmedi"
                : "Aramanıza uygun motor bulunamadı"}
            </p>
          </div>
        ) : (
          <>
            <div className="mb-6 text-sm uppercase tracking-widest text-zinc-500">
              {filtered.length} sonuç bulundu
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filtered.map((m, i) => (
                <MotorcycleCard key={m.id} moto={m} index={i} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Motorcycles;
